// Import d'un devis ou d'une commande en PDF : dépôt du PDF dans le dossier, aperçu des lignes lues avec les contrôles au centime,
// nature du document proposée puis CONFIRMÉE, et l'action qui en découle :
//   • devis reçu d'une autre entreprise → enregistré comme un devis reçu (même écran que l'import tableur) ;
//   • notre devis signé ou ordre d'exécution du client → NOTRE devis aux prix du document, ouvrages types proposés « à confirmer »,
//     puis la confirmation de la commande (émission et acceptation, le PDF servant de « bon pour accord ») ;
//   • import BLOQUÉ tant qu'un total imprimé ne colle pas au centime : la lecture se corrige, chaque correction est tracée.
// L'action principale est en haut de l'écran, atteignable sans défiler sur téléphone.
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, ClipboardCheck, FileText, FileUp, Hammer, PencilLine, ScanText, Trash2, Upload } from 'lucide-react';
import { api, messageErreur } from '../../lib/api.ts';
import { date, dateHeure, euros, pluriel } from '../../lib/format.ts';
import {
  Alerte, Badge, Bouton, Champ, ChampChoix, Chargement, Dialogue, EtatErreur, LienBouton, MarqueIa, Panneau, useNotifier,
} from '../../components/ui.tsx';
import { DossierCadre } from '../etudes/DossierCadre.tsx';
import { quantite } from './commun.tsx';
import { LignesRecues, NotesRecues, ResumeAnalyse, TableauControles, type Analyse } from './devis-recus.tsx';

type Nature = 'devis_recu' | 'notre_devis_signe' | 'ordre_execution';
type Statut = 'illisible' | 'bloque' | 'pret' | 'traite';
type ChampPdf = 'ref' | 'des' | 'u' | 'qte' | 'pu' | 'mt';
const CHAMPS: { k: ChampPdf; libelle: string; num?: boolean }[] = [
  { k: 'ref', libelle: 'Réf.' }, { k: 'des', libelle: 'Désignation' }, { k: 'u', libelle: 'Unité' },
  { k: 'qte', libelle: 'Qté', num: true }, { k: 'pu', libelle: 'P.U. HT', num: true }, { k: 'mt', libelle: 'Montant HT', num: true },
];
interface MotifBlocage { code: string; message: string; lu?: string | null; calcule?: string | null; ecart?: string | null }
interface LigneLue { index: number; page: number; rang: number; ref: string | null; des: string | null; u: string | null; qte: string | null; pu: string | null; mt: string | null; autre: string | null }
type Correction = { op: 'cellule'; index: number; champ: ChampPdf; valeur: string | null } | { op: 'ecarter'; index: number; ecartee: boolean }
  | { op: 'total'; champ: 'ht' | 'tva' | 'ttc'; valeur: string | null };
interface Proposition { ouvrage: { id: string; code: string; label: string; unit: string } | null; heures: string | null; motif: string | null; etat: 'proposee' | 'confirmee' | 'aucune' }
interface VueImport {
  id: string; dossier: { id: string; reference: string; title: string }; fichier: string; pages: number; cree_le: string; cree_par: string | null;
  lecture: 'texte' | 'ia' | 'aucune'; lu_par_ia: { modele: string | null; mention: string } | null;
  lecture_ia: { possible: boolean; raison: string | null; modele: string | null } | null;
  nature: Nature | null; nature_proposee: Nature | null; nature_motifs: string[]; natures: { valeur: Nature; libelle: string }[];
  meta: { entreprise: string | null; reference: string | null; date: string | null; dateAccord: string | null; illisible: string[];
    ecartees: { page: number; rang: number; texte: string; motif: string }[]; depassement: string | null };
  statut: Statut; blocage: MotifBlocage[]; erreur: string | null;
  tableau: LigneLue[]; corrections: (Correction & { par: string; le: string })[];
  analyse: Analyse | null; origine: { numero: number; page: number; rang: number; index: number }[];
  piece: { version_id: string; document_id: string; titre: string; provenance: string | null } | null;
  devis_recu: { id: string; entreprise: string } | null;
  devis: { id: string; numero: string; revision_id: string; statut: string; rev_no: number; acceptation_id: string | null } | null;
  devis_cite: { id: string; numero: string; revision_id: string; rev_no: number } | null;
  propositions: Record<string, Proposition>;
  lignes_devis: { id: string; label: string; quantity: string; unit: string; sale_unit_price?: string | null; work_item_id: string | null; labor_hours: string | null }[];
  droits: { ecrire: boolean; couts: boolean; accepter: boolean; valider: boolean; emettre: boolean };
  deja?: boolean; message?: string;
}
const LIBELLE_STATUT: Record<Statut, { ton: 'succes' | 'danger' | 'ambre' | 'info'; texte: string }> = {
  pret: { ton: 'succes', texte: 'Contrôlé au centime' }, bloque: { ton: 'danger', texte: 'Bloqué : écart à corriger' },
  illisible: { ton: 'ambre', texte: 'Aucune ligne lue' }, traite: { ton: 'info', texte: 'Importé' },
};
const eur = (v: string | null | undefined) => euros(v) ?? '—';

// ═══ Liste dans l'onglet Devis (sous les devis reçus) ═══════════════════════════════════════════════════════════════════
interface ImportListe { id: string; file_name: string; pages: number; lecture: string; nature: Nature | null; nature_proposee: Nature | null; statut: Statut;
  received_quote_id: string | null; quote_id: string | null; created_at: string; total_ht: string | null }
const NATURE_COURTE: Record<Nature, string> = { devis_recu: 'Devis reçu', notre_devis_signe: 'Notre devis signé', ordre_execution: 'Commande du client' };

export function ListeImportsPdf({ dossierId }: { dossierId: string }) {
  const q = useQuery({ queryKey: ['devis-pdf', 'liste', dossierId], queryFn: () => api.get<ImportListe[]>(`/api/dossiers/${dossierId}/devis-pdf`) });
  if (!q.data?.length) return null;
  return (
    <Panneau serre titre="PDF importés (devis et commandes)">
      <div className="liste">
        {q.data.map((x) => (
          <Link key={x.id} to={`/etudes/${dossierId}/devis-pdf/${x.id}`} className="devis-revision-lien devis-recu-lien">
            <span className="devis-lien-titre" style={{ minHeight: 0 }}><FileText size={16} aria-hidden style={{ verticalAlign: '-3px' }} /> {x.file_name}</span>
            <Badge ton={LIBELLE_STATUT[x.statut].ton}>{LIBELLE_STATUT[x.statut].texte}</Badge>
            <span className="petit">{x.nature ? NATURE_COURTE[x.nature] : x.nature_proposee ? `${NATURE_COURTE[x.nature_proposee]} (à confirmer)` : 'Nature à choisir'}</span>
            {x.lecture === 'ia' && <MarqueIa texte="Lu par l’assistance IA" />}
            {x.total_ht && <span className="chiffre petit">{eur(x.total_ht)} HT</span>}
            <span className="tres-petit discret">importé le {date(x.created_at)}</span>
          </Link>
        ))}
      </div>
    </Panneau>
  );
}

// ═══ Dépôt du PDF ═══════════════════════════════════════════════════════════════════════════════════════════════════════
export function PageImportPdf() {
  const { id = '' } = useParams();
  const naviguer = useNavigate();
  const notifier = useNotifier();
  const qc = useQueryClient();
  const [fichier, setFichier] = useState<File | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const lire = async () => {
    if (!fichier) return;
    const fd = new FormData();
    fd.append('fichier', fichier);
    setEnvoi(true); setErreur(null);
    try {
      const r = await api.envoyer<VueImport>(`/api/dossiers/${id}/devis-pdf`, fd);
      if (r.deja && r.message) notifier(r.message); else notifier('PDF lu et déposé dans les pièces du dossier.');
      void qc.invalidateQueries({ queryKey: ['devis-pdf', 'liste', id] });
      naviguer(`/etudes/${id}/devis-pdf/${r.id}`);
    } catch (e) { setErreur(messageErreur(e)); } finally { setEnvoi(false); }
  };
  return (
    <DossierCadre dossierId={id} actif="devis" actions={<LienBouton to={`/etudes/${id}/devis`} icone={<FileText aria-hidden />}>Tous les devis</LienBouton>}>
      <div className="pile-l devis-page">
        <Panneau titre="Importer un PDF">
          <div className="pile">
            <label className={`btn devis-fichier ${fichier ? '' : 'btn-principal'}`}>
              <FileUp aria-hidden /> {fichier ? fichier.name : 'Choisir le PDF'}
              <input type="file" accept="application/pdf,.pdf" className="devis-fichier-saisie"
                onChange={(e) => { setFichier(e.target.files?.[0] ?? null); setErreur(null); }} />
            </label>
            {fichier && <Bouton variante="principal" icone={<Upload aria-hidden />} chargement={envoi} onClick={lire}>Lire le PDF</Bouton>}
            {erreur && <Alerte ton="danger">{erreur}</Alerte>}
            <p className="petit secondaire">Devis reçu d’une autre entreprise, notre devis signé, ordre d’exécution ou bon de commande du client : 10 Mo et 40 pages
              au plus. Le PDF est lu en colonnes (Réf., Désignation, Unité, Qté, P.U., Montant) puis contrôlé au centime ; il est conservé dans les pièces du dossier.</p>
            <p className="tres-petit discret">Un PDF scanné, sans texte, ne peut être lu que par l’assistance IA, si l’administrateur l’a autorisée. Aucun chiffre n’est deviné :
              si le total recalculé ne colle pas au total imprimé, l’import reste bloqué en brouillon avec l’écart affiché.</p>
          </div>
        </Panneau>
      </div>
    </DossierCadre>
  );
}

// ═══ Brouillon d'import ═════════════════════════════════════════════════════════════════════════════════════════════════
export function PageBrouillonPdf() {
  const { id = '', pid = '' } = useParams();
  const qc = useQueryClient();
  const notifier = useNotifier();
  const naviguer = useNavigate();
  const q = useQuery({ queryKey: ['devis-pdf', pid], queryFn: () => api.get<VueImport>(`/api/devis-pdf/${pid}`) });
  const [corriger, setCorriger] = useState(false);
  const [confirmation, setConfirmation] = useState<null | 'acceptation' | 'ia'>(null);
  const [entreprise, setEntreprise] = useState<string | null>(null);
  const correctionRef = useRef<HTMLDivElement>(null);
  const v = q.data;
  useEffect(() => { if (v && entreprise === null) setEntreprise(v.meta.entreprise ?? ''); }, [v, entreprise]);
  const rafraichir = (x?: VueImport) => {
    if (x) qc.setQueryData(['devis-pdf', pid], x); else void qc.invalidateQueries({ queryKey: ['devis-pdf', pid] });
    void qc.invalidateQueries({ queryKey: ['devis-pdf', 'liste', id] });
  };
  const patch = useMutation({
    mutationFn: (corps: Record<string, unknown>) => api.patch<VueImport>(`/api/devis-pdf/${pid}`, corps),
    onSuccess: (x) => rafraichir(x), onError: (e) => notifier(messageErreur(e), true),
  });
  const action = useMutation({
    mutationFn: async (quoi: 'recu' | 'devis' | 'acceptation' | 'acceptation_citee' | 'ia' | 'supprimer') => {
      if (quoi === 'recu') return api.post<{ devisRecuId: string }>(`/api/devis-pdf/${pid}/devis-recu`, {});
      if (quoi === 'devis') return api.post<{ quoteId: string; numero: string; lignes: number; ouvrages_proposes: number }>(`/api/devis-pdf/${pid}/devis`, {});
      if (quoi === 'acceptation') return api.post<{ quoteId: string; etapes: string[]; acceptedOn: string }>(`/api/devis-pdf/${pid}/acceptation`, {});
      if (quoi === 'acceptation_citee') return api.post<{ quoteId: string; etapes: string[]; acceptedOn: string }>(`/api/devis-pdf/${pid}/acceptation`, { revisionId: v?.devis_cite?.revision_id });
      if (quoi === 'ia') return api.post<VueImport>(`/api/devis-pdf/${pid}/lecture-ia`, {});
      return api.del(`/api/devis-pdf/${pid}`);
    },
    onSuccess: (r: any, quoi) => {
      setConfirmation(null);
      if (quoi === 'recu') { notifier('Devis reçu enregistré : comparez-le à vos ouvrages types.'); naviguer(`/etudes/${id}/devis-recus/${r.devisRecuId}?vue=comparaison`); return; }
      if (quoi === 'devis') notifier(`Devis ${r.numero} créé en brouillon aux prix du document (${pluriel(r.lignes, 'ligne')}). ${r.ouvrages_proposes ? 'Confirmez les ouvrages types proposés.' : ''}`);
      if (quoi === 'acceptation' || quoi === 'acceptation_citee') notifier(`Acceptation enregistrée le ${date(r.acceptedOn)}${r.etapes.length ? ` (devis ${r.etapes.length > 1 ? 'soumis, validé et émis' : 'émis'})` : ''} : le chantier peut être préparé.`);
      if (quoi === 'supprimer') { notifier('Import supprimé ; le PDF reste dans les pièces du dossier.'); void qc.invalidateQueries({ queryKey: ['devis-pdf', 'liste', id] }); naviguer(`/etudes/${id}/devis`); return; }
      void qc.invalidateQueries({ queryKey: ['devis-dossier', id] });
      rafraichir(quoi === 'ia' ? r : undefined);
    },
    onError: (e) => { setConfirmation(null); notifier(messageErreur(e), true); },
  });
  const numeroLu = useMemo(() => new Map((v?.origine ?? []).map((o) => [o.numero, o])), [v]);

  if (q.isLoading) return <DossierCadre dossierId={id} actif="devis"><Chargement lignes={5} /></DossierCadre>;
  if (q.isError || !v) return <DossierCadre dossierId={id} actif="devis"><EtatErreur erreur={q.error} reessayer={() => q.refetch()} /></DossierCadre>;
  const a = v.analyse;
  const lignesPrix = a?.resume.nbLignesPrix ?? 0;
  // Lignes affichées avec le numéro de la lecture (le même que dans « Corriger la lecture »).
  const lignesAffichees = (a?.lignes ?? []).map((l) => { const o = l.numero === undefined ? undefined : numeroLu.get(l.numero); return { ...l, numero: o && o.index >= 0 ? o.index + 1 : l.numero }; });
  const s = LIBELLE_STATUT[v.statut];
  const allerCorriger = () => { setCorriger(true); setTimeout(() => correctionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50); };
  const enCours = action.isPending || patch.isPending;

  // ─ Action principale (une seule, en haut) ─
  let principale: ReactNode = null;
  if (!v.droits.ecrire) principale = null;
  else if (v.devis_recu) principale = <LienBouton variante="principal" to={`/etudes/${id}/devis-recus/${v.devis_recu.id}`} icone={<ClipboardCheck aria-hidden />}>Ouvrir le devis reçu</LienBouton>;
  else if (v.devis?.acceptation_id) principale = <LienBouton variante="principal" to={`/etudes/${id}/devis/${v.devis.id}?revision=${v.devis.revision_id}`} icone={<Hammer aria-hidden />}>Préparer le chantier</LienBouton>;
  else if (v.devis) principale = v.droits.accepter
    ? <Bouton variante="principal" icone={<CheckCircle2 aria-hidden />} chargement={action.isPending} onClick={() => setConfirmation('acceptation')}>Confirmer la commande</Bouton>
    : <LienBouton variante="principal" to={`/etudes/${id}/devis/${v.devis.id}`} icone={<FileText aria-hidden />}>Ouvrir le devis {v.devis.numero}</LienBouton>;
  else if (v.statut === 'illisible') principale = v.lecture_ia?.possible
    ? <Bouton variante="principal" icone={<ScanText aria-hidden />} chargement={action.isPending} onClick={() => setConfirmation('ia')}>Faire lire ce PDF par l’assistance IA</Bouton>
    : <LienBouton variante="principal" to={`/etudes/${id}/devis-recus/import`} icone={<Upload aria-hidden />}>Importer le tableur à la place</LienBouton>;
  else if (!v.nature) principale = v.nature_proposee
    ? <Bouton variante="principal" icone={<CheckCircle2 aria-hidden />} chargement={patch.isPending} onClick={() => patch.mutate({ nature: v.nature_proposee })}>
        Confirmer : {NATURE_COURTE[v.nature_proposee].toLowerCase()}</Bouton>
    : null;
  else if (v.statut === 'bloque') principale = <Bouton variante="principal" icone={<PencilLine aria-hidden />} onClick={allerCorriger}>Corriger la lecture</Bouton>;
  else if (v.nature === 'devis_recu') principale = (
    <Bouton variante="principal" icone={<Upload aria-hidden />} chargement={action.isPending} disabled={!entreprise?.trim()}
      onClick={async () => { if (entreprise !== (v.meta.entreprise ?? '')) await patch.mutateAsync({ entreprise }); action.mutate('recu'); }}>
      {entreprise?.trim() ? `Enregistrer le devis reçu (${pluriel(lignesPrix, 'ligne')})` : 'Indiquez l’entreprise ci-dessous'}</Bouton>);
  else if (v.nature === 'notre_devis_signe' && v.devis_cite && v.droits.accepter) principale = (
    <Bouton variante="principal" icone={<CheckCircle2 aria-hidden />} chargement={action.isPending} onClick={() => action.mutate('acceptation_citee')}>
      {`Enregistrer l’acceptation du devis ${v.devis_cite.numero}`}</Bouton>);
  else if (v.droits.couts) principale = (
    <Bouton variante="principal" icone={<Hammer aria-hidden />} chargement={action.isPending} onClick={() => action.mutate('devis')}>
      {`Créer notre devis (${pluriel(lignesPrix, 'ligne')}, ${eur(a?.totalHtLu)} HT)`}</Bouton>);

  return (
    <DossierCadre dossierId={id} actif="devis" large actions={<LienBouton to={`/etudes/${id}/devis`} icone={<FileText aria-hidden />}>Tous les devis</LienBouton>}>
      <div className="pile-l devis-page">
        <section className="panneau devis-barre devis-pdf-barre" aria-label="Import du PDF">
          <div className="pile" style={{ gap: 6, minWidth: 0 }}>
            <h2 className="devis-titre devis-pdf-titre">{v.fichier}</h2>
            <span className="rangee" style={{ gap: 6 }}>
              <Badge ton={s.ton}>{s.texte}</Badge>
              {v.lu_par_ia ? <MarqueIa texte="Lu par l’assistance IA — à vérifier sur le PDF" /> : v.lecture === 'texte' ? <Badge>Lu sur la couche texte</Badge> : null}
              {a && <span className="petit chiffre">{pluriel(lignesPrix, 'ligne de prix', 'lignes de prix')} — {eur(a.totalHtLu ?? a.totalHtCalcule)} HT</span>}
            </span>
          </div>
          {v.statut === 'bloque' && v.blocage[0] && <Alerte ton="danger">{v.blocage[0].message}{v.blocage.length > 1 ? ` (+ ${pluriel(v.blocage.length - 1, 'autre motif')})` : ''}</Alerte>}
          {v.statut === 'illisible' && (
            <Alerte ton="ambre">{v.erreur ?? 'Aucune ligne lue.'} {v.lecture === 'aucune' ? 'Ce PDF n’a pas de couche texte (document scanné).' : ''}
              {v.lecture_ia && !v.lecture_ia.possible && <> {v.lecture_ia.raison}</>}</Alerte>
          )}
          {principale && <div className="groupe-btn devis-pdf-principale">{principale}</div>}
          {v.droits.ecrire && !v.devis && !v.devis_recu && v.statut !== 'illisible' && (
            <div className="devis-pdf-nature">
              <ChampChoix libelle="Nature du document" vide="À choisir" value={v.nature ?? ''} disabled={patch.isPending}
                options={v.natures.map((n) => ({ valeur: n.valeur, libelle: n.libelle }))}
                aide={v.nature ? 'Confirmée.' : v.nature_proposee ? `Proposée : ${v.natures.find((n) => n.valeur === v.nature_proposee)?.libelle} — ${v.nature_motifs.join(' ; ')}. À confirmer.` : v.nature_motifs.join(' ; ')}
                onChange={(e) => patch.mutate({ nature: e.target.value || null })} />
              {v.nature === 'devis_recu' && (
                <Champ libelle="Entreprise qui a émis le devis" required maxLength={200} value={entreprise ?? ''} onChange={(e) => setEntreprise(e.target.value)}
                  onBlur={() => { if (entreprise !== (v.meta.entreprise ?? '')) patch.mutate({ entreprise }); }} aide={v.meta.entreprise ? 'Lue en tête du PDF — à vérifier' : undefined} />
              )}
            </div>
          )}
          {v.devis && !v.devis.acceptation_id && v.droits.accepter && (
            <p className="tres-petit secondaire">Devis <Link to={`/etudes/${id}/devis/${v.devis.id}`} className="mono">{v.devis.numero}</Link> créé aux prix du document.
              « Confirmer la commande » le soumet, le valide et l’émet, puis enregistre l’acceptation avec ce PDF comme bon pour accord.</p>
          )}
        </section>

        {v.devis && <PanneauOuvrages v={v} pid={pid} />}

        {a && (
          <Panneau titre="Contrôles au centime">
            <div className="pile">
              <ResumeAnalyse resume={a.resume} />
              {v.blocage.length > 1 && <ul className="devis-liste-simple">{v.blocage.map((b, i) => <li key={i} className="petit devis-motif">{b.message}</li>)}</ul>}
              <TableauControles controles={a.controles} />
            </div>
          </Panneau>
        )}
        {a && (
          <Panneau serre titre={pluriel(lignesPrix, 'ligne de prix lue', 'lignes de prix lues')} actions={v.lu_par_ia ? <MarqueIa texte="Lu par l’assistance IA" /> : undefined}>
            <LignesRecues lignes={lignesAffichees as any} />
          </Panneau>
        )}
        {a && !!a.notes.length && <Panneau titre="Mentions écartées des prix"><NotesRecues notes={a.notes} /></Panneau>}

        {v.droits.ecrire && v.statut !== 'traite' && !!v.tableau.length && (
          <div ref={correctionRef} className={v.statut === 'bloque' ? undefined : 'expert-seulement'}>
            <PanneauCorrections v={v} ouvert={corriger || v.statut === 'bloque'} basculer={() => setCorriger((x) => !x)} envoyer={(c) => patch.mutate({ corrections: c })} enCours={enCours} />
          </div>
        )}

        <Panneau titre="Pièce du dossier">
          <div className="pile" style={{ gap: 4 }}>
            {v.piece
              ? <span className="petit"><Link to={`/etudes/${id}/pieces`}>{v.piece.titre}</Link>{v.piece.provenance ? ` — ${v.piece.provenance}` : ''}</span>
              : <span className="petit discret">Pièce introuvable.</span>}
            <span className="tres-petit discret">{pluriel(v.pages, 'page')} — importé le {dateHeure(v.cree_le)}{v.cree_par ? ` par ${v.cree_par}` : ''}
              {v.meta.reference ? ` — n° lu : ${v.meta.reference}` : ''}{v.meta.date ? ` — daté du ${date(v.meta.date)}` : ''}</span>
            {!!v.meta.ecartees.length && <span className="tres-petit discret">{pluriel(v.meta.ecartees.length, 'ligne d’habillage écartée', 'lignes d’habillage écartées')} (en-tête, pied ou numéro de page répété).</span>}
            {v.meta.depassement && <span className="tres-petit devis-motif">Lecture limitée : {v.meta.depassement}.</span>}
            {v.droits.ecrire && v.statut !== 'traite' && (
              <div className="groupe-btn"><Bouton variante="danger" compact icone={<Trash2 aria-hidden />} chargement={action.isPending && action.variables === 'supprimer'}
                onClick={() => action.mutate('supprimer')}>Supprimer cet import</Bouton></div>
            )}
          </div>
        </Panneau>
        {v.lu_par_ia && <p className="tres-petit discret" data-aiact="lecture-pdf">{v.lu_par_ia.mention}{v.lu_par_ia.modele ? ` Modèle : ${v.lu_par_ia.modele}.` : ''}</p>}
      </div>

      <Dialogue titre="Confirmer la commande" ouvert={confirmation === 'acceptation'} fermer={() => setConfirmation(null)}
        pied={<><Bouton onClick={() => setConfirmation(null)}>Annuler</Bouton>
          <Bouton variante="principal" chargement={action.isPending} onClick={() => action.mutate('acceptation')}>Émettre et enregistrer l’acceptation</Bouton></>}>
        <div className="pile">
          <p>Le devis {v.devis?.numero} sera soumis, validé et émis (PDF figé), puis marqué accepté par le client avec ce PDF comme pièce justificative.</p>
          <p className="petit secondaire">Date d’acceptation : {v.meta.dateAccord ? `celle du document (${date(v.meta.dateAccord)}) si elle n’est pas antérieure à l’émission, sinon` : ''} la date du jour.
            La préparation du chantier s’ouvre ensuite depuis le devis.</p>
        </div>
      </Dialogue>
      <Dialogue titre="Lecture par l’assistance IA" ouvert={confirmation === 'ia'} fermer={() => setConfirmation(null)}
        pied={<><Bouton onClick={() => setConfirmation(null)}>Annuler</Bouton>
          <Bouton variante="principal" chargement={action.isPending} onClick={() => action.mutate('ia')}>Envoyer le PDF à l’assistance</Bouton></>}>
        <p>Le PDF ({pluriel(v.pages, 'page')}) est envoyé à l’assistance IA{v.lecture_ia?.modele ? ` (${v.lecture_ia.modele})` : ''}, autorisée par l’administrateur. Elle transcrit le tableau ;
          TRAME refait tous les contrôles au centime et bloque l’import en cas d’écart. Les lignes lues restent marquées « lues par l’assistance IA ».</p>
      </Dialogue>
    </DossierCadre>
  );
}

// ─── Ouvrages types proposés « à confirmer » (notre devis créé à partir du document) ───
function PanneauOuvrages({ v, pid }: { v: VueImport; pid: string }) {
  const qc = useQueryClient();
  const notifier = useNotifier();
  const ouvrages = useQuery({ queryKey: ['ouvrages'], enabled: v.droits.couts, queryFn: () => api.get<{ ouvrages: { id: string; label: string; unit: string; active: boolean }[] }>('/api/ouvrages') });
  const choisir = useMutation({
    mutationFn: (b: { ligneId: string; workItemId: string | null }) => api.post(`/api/devis-pdf/${pid}/ouvrage`, b),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['devis-pdf', pid] }), onError: (e) => notifier(messageErreur(e), true),
  });
  const modifiable = v.devis?.statut === 'brouillon' && v.droits.ecrire && v.droits.couts;
  const lignes = v.lignes_devis;
  if (!lignes.length) return null;
  const opts = (ouvrages.data?.ouvrages ?? []).filter((o) => o.active).map((o) => ({ valeur: o.id, libelle: `${o.label} (${o.unit})` }));
  const aConfirmer = lignes.filter((l) => v.propositions[l.id]?.etat === 'proposee').length;
  return (
    <Panneau titre={`Notre devis ${v.devis?.numero ?? ''} — ouvrages types`} actions={aConfirmer ? <Badge ton="ambre">{pluriel(aConfirmer, 'proposition à confirmer', 'propositions à confirmer')}</Badge> : undefined}>
      <div className="pile">
        <p className="tres-petit discret">Prix du document repris tels quels (prix convenus). Un ouvrage type confirmé apporte ses temps de pose (préparation du chantier) ; il ne change pas le prix.</p>
        <ul className="devis-recu-comparaison">
          {lignes.map((l) => {
            const p = v.propositions[l.id];
            return (
              <li key={l.id} className="devis-recu-cmp">
                <div className="pile" style={{ gap: 2, minWidth: 0 }}>
                  <span className="petit">{l.label}</span>
                  <span className="tres-petit secondaire chiffre">{quantite(l.quantity)} {l.unit} × {'sale_unit_price' in l ? <>{eur(l.sale_unit_price)} (prix convenu)</> : 'prix réservé à la direction'}</span>
                </div>
                <div className="devis-recu-cmp-ouvrage">
                  {modifiable
                    ? <ChampChoix libelle="Ouvrage type" value={p?.etat === 'confirmee' ? (p.ouvrage?.id ?? '') : p?.etat === 'aucune' ? '' : '__prop'}
                        onChange={(e) => { const x = e.target.value; if (x !== '__prop') choisir.mutate({ ligneId: l.id, workItemId: x || null }); }}
                        options={[...(p?.etat === 'proposee' && p.ouvrage ? [{ valeur: '__prop', libelle: `Proposé : ${p.ouvrage.label} — à confirmer` }] : []),
                          { valeur: '', libelle: 'Aucun ouvrage' }, ...opts]} />
                    : <span className="petit">{p?.ouvrage ? `${p.ouvrage.label}${p.etat === 'proposee' ? ' (proposé)' : ''}` : 'Aucun ouvrage'}</span>}
                </div>
                <div className="devis-recu-cmp-heures">
                  {p?.etat === 'proposee' && p.ouvrage && modifiable && (
                    <Bouton compact icone={<CheckCircle2 aria-hidden />} chargement={choisir.isPending && choisir.variables?.ligneId === l.id}
                      onClick={() => choisir.mutate({ ligneId: l.id, workItemId: p.ouvrage!.id })}>Confirmer</Bouton>
                  )}
                  {p?.etat === 'confirmee' && <Badge ton="succes">Confirmé</Badge>}
                  {p?.heures ? <span className="tres-petit secondaire">{quantite(p.heures)} h (catalogue)</span> : p?.motif ? <span className="tres-petit devis-motif">{p.motif}</span> : null}
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </Panneau>
  );
}

// ─── Corriger la lecture (tracé, rejoué sur la lecture d'origine) ───
function PanneauCorrections({ v, ouvert, basculer, envoyer, enCours }:
  { v: VueImport; ouvert: boolean; basculer: () => void; envoyer: (c: Correction[]) => void; enCours: boolean }) {
  const [saisies, setSaisies] = useState<Record<string, string | null>>({});
  const [ecartees, setEcartees] = useState<Record<number, boolean>>({});
  const [totaux, setTotaux] = useState<Record<'ht' | 'tva' | 'ttc', string>>({ ht: '', tva: '', ttc: '' });
  // État courant des écarts (corrections déjà enregistrées).
  const dejaEcartees = useMemo(() => {
    const e = new Set<number>();
    for (const c of v.corrections) if (c.op === 'ecarter') { if (c.ecartee) e.add(c.index); else e.delete(c.index); }
    return e;
  }, [v.corrections]);
  const valeurCourante = (l: LigneLue, k: ChampPdf) => {
    let x = l[k];
    for (const c of v.corrections) if (c.op === 'cellule' && c.index === l.index && c.champ === k) x = c.valeur;
    return x;
  };
  const modifs: Correction[] = [
    ...Object.entries(saisies).map(([cle, valeur]) => { const [i, k] = cle.split(':'); return { op: 'cellule' as const, index: Number(i), champ: k as ChampPdf, valeur: valeur?.trim() ? valeur.trim() : null }; }),
    ...Object.entries(ecartees).filter(([i, e]) => e !== dejaEcartees.has(Number(i))).map(([i, e]) => ({ op: 'ecarter' as const, index: Number(i), ecartee: e })),
    ...(['ht', 'tva', 'ttc'] as const).filter((k) => totaux[k].trim()).map((k) => ({ op: 'total' as const, champ: k, valeur: totaux[k].trim() })),
  ];
  return (
    <Panneau titre="Corriger la lecture" actions={<Bouton compact icone={<PencilLine aria-hidden />} onClick={basculer} aria-expanded={ouvert}>{ouvert ? 'Masquer' : 'Afficher'}</Bouton>}>
      {!ouvert ? <p className="tres-petit discret">Lignes telles que lues sur le PDF ; chaque correction est conservée avec son auteur, et la lecture corrigée repasse par les mêmes contrôles.</p> : (
        <div className="pile">
          <p className="tres-petit discret">Corrigez d’après le PDF, sans rien inventer : une cellule mal lue, une ligne parasite à écarter, ou un total imprimé à saisir s’il n’a pas été lu.
            Si le PDF lui-même est faux, l’import reste bloqué : demandez un document corrigé.</p>
          <div className="tableau-conteneur">
            <table className="tableau devis-pdf-corrections">
              <thead><tr><th scope="col" className="num">N°</th><th scope="col">Page</th>{CHAMPS.map((c) => <th key={c.k} scope="col" className={c.num ? 'num' : undefined}>{c.libelle}</th>)}<th scope="col">Écarter</th></tr></thead>
              <tbody>
                {v.tableau.map((l) => {
                  const ecartee = ecartees[l.index] ?? dejaEcartees.has(l.index);
                  return (
                    <tr key={l.index} className={ecartee ? 'devis-pdf-ecartee' : undefined}>
                      <td className="num tres-petit discret">{l.index + 1}</td>
                      <td className="tres-petit discret">p. {l.page}</td>
                      {CHAMPS.map((c) => {
                        const cle = `${l.index}:${c.k}`;
                        const val = cle in saisies ? saisies[cle] : valeurCourante(l, c.k);
                        return (
                          <td key={c.k} className={c.num ? 'num' : undefined}>
                            <input className={`saisie devis-pdf-cellule ${c.k === 'des' ? 'devis-pdf-des' : ''} ${c.num ? 'saisie-mono' : ''}`} aria-label={`${c.libelle}, ligne ${l.index + 1}`}
                              value={val ?? ''} maxLength={c.k === 'des' ? 1000 : 60} onChange={(e) => setSaisies((x) => ({ ...x, [cle]: e.target.value }))} />
                          </td>
                        );
                      })}
                      <td><input type="checkbox" className="devis-pdf-case" aria-label={`Écarter la ligne ${l.index + 1}`} checked={ecartee}
                        onChange={(e) => setEcartees((x) => ({ ...x, [l.index]: e.target.checked }))} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="grille-3">
            {(['ht', 'tva', 'ttc'] as const).map((k) => (
              <Champ key={k} libelle={`${k === 'ht' ? 'Total HT' : k === 'tva' ? 'TVA' : 'Total TTC'} imprimé (s’il n’a pas été lu)`} mono inputMode="decimal" maxLength={40}
                value={totaux[k]} onChange={(e) => setTotaux((x) => ({ ...x, [k]: e.target.value }))} placeholder="ex. 12 345,67" />
            ))}
          </div>
          <div className="groupe-btn">
            <Bouton icone={<CheckCircle2 aria-hidden />} chargement={enCours} disabled={!modifs.length}
              onClick={() => { envoyer(modifs); setSaisies({}); setEcartees({}); setTotaux({ ht: '', tva: '', ttc: '' }); }}>
              {modifs.length ? `Enregistrer ${pluriel(modifs.length, 'correction')} et relire` : 'Aucune correction'}
            </Bouton>
          </div>
          {!!v.corrections.length && (
            <details>
              <summary className="petit">{pluriel(v.corrections.length, 'correction enregistrée', 'corrections enregistrées')}</summary>
              <ul className="devis-liste-simple">
                {v.corrections.map((c, i) => (
                  <li key={i} className="tres-petit">{c.op === 'cellule' ? `Ligne ${c.index + 1}, ${CHAMPS.find((x) => x.k === c.champ)?.libelle} → « ${c.valeur ?? 'vide'} »`
                    : c.op === 'ecarter' ? `Ligne ${c.index + 1} ${c.ecartee ? 'écartée' : 'reprise'}` : `${c.champ.toUpperCase()} saisi : ${c.valeur ?? 'retiré'}`} — {c.par}, {dateHeure(c.le)}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </Panneau>
  );
}

